import type { SpinResponse } from "@slot/client-engine";
import type { ScriptedFixtureCatalog } from "@slot/game-shell";
import { BASE_SCRIPTS, BUY_SCRIPTS, MODE_SCRIPTS, SESSION_SCRIPTS } from "./fixtureScripts";
import { buildBuyRound, buildNaturalRound } from "./TeddyRoundBuilder";

/** Every offline round by its id (the fixture-script key): built afresh per call, as the provider expects. */
export const FIXTURES = {
  ...mapValues(BASE_SCRIPTS, (script) => () => buildNaturalRound(script)),
  ...mapValues(SESSION_SCRIPTS, (script) => () => buildNaturalRound(script)),
  ...mapValues(MODE_SCRIPTS, (script) => () => buildNaturalRound(script)),
  ...mapValues(BUY_SCRIPTS, (script) => () => buildBuyRound(script)),
} as const;
export type FixtureId = keyof typeof FIXTURES;

/**
 * The offline server's catalog (ids are the server contract, see feature/createFeaturePurchaseOffers.ts):
 * - baseFixtures: the base game's round robin (hand-ordered for playing offline; some rounds come round twice);
 * - featureFixturesById: the bought sessions, routed by `requestedFeatureIdentifier`;
 * - baseFixturesByModeId: each feature mode's base pool, routed by `requestedFeatureModeIdentifier`.
 */
export function createFixtureCatalog(): ScriptedFixtureCatalog {
  const base: FixtureId[] = [
    "reel_no_win",
    "reel_line_win",
    "reel_vs_battle",
    "reel_anticipation",
    "reel_three_wins",
    "reel_grouped_win",
    "reel_vs_no_battle",
    "reel_zigzag",
    "reel_nice_win",
    "reel_wild_win",
    "fs_vs_rush",
    "reel_vs_double",
    "reel_no_win",
    "reel_big_win",
    "reel_anticipation",
    "fs_super_bonus",
    "reel_huge_win",
  ];
  return {
    baseFixtures: base.map((id) => FIXTURES[id]()),
    featureFixtures: [FIXTURES.buy_vs_rush(), FIXTURES.buy_super_bonus()],
    featureFixturesById: {
      vs_rush_buy: [FIXTURES.buy_vs_rush()],
      super_bonus_buy: [FIXTURES.buy_super_bonus()],
    },
    baseFixturesByModeId: {
      bonus_hunt_mode: [FIXTURES.bonus_hunt_tease(), FIXTURES.bonus_hunt_tease(), FIXTURES.bonus_hunt_trigger()],
      guaranteed_vs_mode: [FIXTURES.guaranteed_vs_battle(), FIXTURES.guaranteed_vs_no_battle()],
    },
  };
}

function mapValues<T extends Record<string, unknown>>(record: T, map: (value: T[keyof T]) => () => SpinResponse): { [K in keyof T]: () => SpinResponse } {
  return Object.fromEntries(Object.entries(record).map(([key, value]) => [key, map(value as T[keyof T])])) as { [K in keyof T]: () => SpinResponse };
}
